/**
 * Sandbox-side mirror of the spec frontmatter codec.
 *
 * Lighter than the sandbox-ai version — we only need parse + serialize
 * for the panel's read/write loop. Validation is more lenient: bad
 * specs are surfaced with a `reason` so the UI can render an error
 * row, rather than thrown.
 *
 * KEEP IN SYNC with `packages/sandbox-ai/src/design/specs/frontmatter.ts`
 * — both files read and write the same on-disk format.
 */

import {
  ARCHIVE_SENTINEL_SPEC_ID,
  DESIGN_SPEC_CATEGORIES,
  type CodeSymbolRef,
  type DesignSpec,
  type DesignSpecCategory,
} from './types';

const FRONTMATTER_REGEX = /^<!--design-spec\s*\n([\s\S]*?)\n-->\s*\n?/;

export interface ParseOk { ok: true; spec: DesignSpec; }
export interface ParseFail { ok: false; reason: string; }

export function parseSpec(content: string): ParseOk | ParseFail {
  const match = content.match(FRONTMATTER_REGEX);
  if (!match) return { ok: false, reason: 'missing or malformed design-spec frontmatter' };

  let raw: unknown;
  try {
    raw = JSON.parse(match[1]);
  } catch (err) {
    return { ok: false, reason: `frontmatter is not valid JSON: ${(err as Error).message}` };
  }
  if (!isObj(raw)) return { ok: false, reason: 'frontmatter root must be an object' };

  const id = raw['id'];
  if (typeof id !== 'string' || !isWellFormedSpecId(id)) {
    return { ok: false, reason: `invalid spec id: ${JSON.stringify(id)}` };
  }
  if (typeof raw['title'] !== 'string' || raw['title'].trim().length === 0) {
    return { ok: false, reason: 'title is required' };
  }
  const category = raw['category'];
  if (typeof category !== 'string' || !isCategory(category)) {
    return { ok: false, reason: `invalid category: ${JSON.stringify(category)}` };
  }

  const refs = parseRefs(raw['relatedCodeSymbols']);
  if (!refs.ok) return refs;
  const linked = parseLinks(raw['relatedSpecIds']);
  if (!linked.ok) return linked;

  if (typeof raw['createdAt'] !== 'string' || isNaN(Date.parse(raw['createdAt']))) {
    return { ok: false, reason: 'createdAt must be an ISO 8601 string' };
  }
  if (typeof raw['updatedAt'] !== 'string' || isNaN(Date.parse(raw['updatedAt']))) {
    return { ok: false, reason: 'updatedAt must be an ISO 8601 string' };
  }

  const spec: DesignSpec = {
    id,
    title: raw['title'],
    category,
    relatedCodeSymbols: refs.value,
    relatedSpecIds: linked.value,
    createdAt: raw['createdAt'] as string,
    updatedAt: raw['updatedAt'] as string,
    body: content.slice(match[0].length).trim(),
  };
  if (typeof raw['systemName'] === 'string') spec.systemName = raw['systemName'];
  if (typeof raw['supersedes'] === 'string' && isWellFormedSpecId(raw['supersedes'])) {
    spec.supersedes = raw['supersedes'];
  }
  if (typeof raw['supersededBy'] === 'string' && isWellFormedSpecId(raw['supersededBy'])) {
    spec.supersededBy = raw['supersededBy'];
  }
  return { ok: true, spec };
}

export function serializeSpec(spec: DesignSpec): string {
  const meta: Record<string, unknown> = {
    id: spec.id,
    title: spec.title,
    category: spec.category,
    relatedCodeSymbols: spec.relatedCodeSymbols ?? [],
    relatedSpecIds: spec.relatedSpecIds ?? [],
    createdAt: spec.createdAt,
    updatedAt: spec.updatedAt,
  };
  if (spec.systemName !== undefined) meta['systemName'] = spec.systemName;
  if (spec.supersedes !== undefined) meta['supersedes'] = spec.supersedes;
  if (spec.supersededBy !== undefined) meta['supersededBy'] = spec.supersededBy;
  return `<!--design-spec\n${JSON.stringify(meta, null, 2)}\n-->\n\n${spec.body.trim()}\n`;
}

// ─── Helpers ─────────────────────────────────────────────────────────

export function isWellFormedSpecId(id: string): boolean {
  return /^spec_[0-9a-f]{8}$/.test(id);
}

export function isWritableSpecId(id: string): boolean {
  return isWellFormedSpecId(id) && id !== ARCHIVE_SENTINEL_SPEC_ID;
}

function isCategory(v: string): v is DesignSpecCategory {
  return (DESIGN_SPEC_CATEGORIES as readonly string[]).includes(v);
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseRefs(v: unknown): { ok: true; value: CodeSymbolRef[] } | ParseFail {
  if (v === undefined || v === null) return { ok: true, value: [] };
  if (!Array.isArray(v)) return { ok: false, reason: 'relatedCodeSymbols must be an array' };
  const out: CodeSymbolRef[] = [];
  for (let i = 0; i < v.length; i++) {
    const entry = v[i];
    if (!isObj(entry) || typeof entry['path'] !== 'string' || entry['path'].length === 0) {
      return { ok: false, reason: `relatedCodeSymbols[${i}] must have a non-empty path` };
    }
    const ref: CodeSymbolRef = { path: entry['path'] };
    if (typeof entry['symbol'] === 'string' && entry['symbol'].length > 0) {
      ref.symbol = entry['symbol'];
    }
    out.push(ref);
  }
  return { ok: true, value: out };
}

function parseLinks(v: unknown): { ok: true; value: string[] } | ParseFail {
  if (v === undefined || v === null) return { ok: true, value: [] };
  if (!Array.isArray(v)) return { ok: false, reason: 'relatedSpecIds must be an array' };
  const out: string[] = [];
  for (let i = 0; i < v.length; i++) {
    if (typeof v[i] !== 'string' || !isWellFormedSpecId(v[i])) {
      return { ok: false, reason: `relatedSpecIds[${i}] must be a valid spec id` };
    }
    out.push(v[i]);
  }
  return { ok: true, value: out };
}

/** Generate a fresh spec id, retrying against an `inUse` predicate. */
export function generateSpecId(inUse?: (id: string) => boolean): string {
  for (let i = 0; i < 8; i++) {
    const hex = Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
    const id = `spec_${hex}`;
    if (id === ARCHIVE_SENTINEL_SPEC_ID) continue;
    if (!inUse || !inUse(id)) return id;
  }
  throw new Error('generateSpecId: 8 collisions in a row');
}
